import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { useLanguage } from '@/hooks/useLanguage';

const heroImage = '/hero-rollski.webp';
const currentCover = '/magazines/cover-04-2026.jpg';

const Hero = () => {
  const { language } = useLanguage();

  const headline = language === 'en'
    ? 'You think of snow before others reach for their winter jacket.'
    : 'Du denkst an Schnee, bevor andere ihre Winterjacke suchen.';

  const sub = language === 'en'
    ? 'glide is the Swiss magazine for everyone who doesn\'t just do cross-country skiing — they live it.'
    : 'glide ist das Schweizer Magazin für alle, die Langlauf nicht nur machen – sondern leben.';

  const handleExplore = (e: React.MouseEvent) => {
    e.preventDefault();
    const el = document.getElementById('stories');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <section className="relative min-h-[92vh] w-full overflow-hidden flex items-end bg-ink">
      {/* Cinematic image */}
      <img
        src={heroImage}
        alt=""
        className="absolute inset-0 w-full h-full object-cover object-center"
        loading="eager"
        fetchPriority="high"
        decoding="sync"
      />
      {/* Ink gradient for legibility */}
      <div className="absolute inset-0 bg-ink-gradient" />
      <div className="absolute inset-0 bg-ink/25" />

      <div className="relative z-10 w-full max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 pb-14 sm:pb-20 lg:pb-24 pt-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end">
          {/* Text column */}
          <div className="lg:col-span-8 text-white">
            <span className="inline-flex items-center gap-2 mb-6 text-[10px] sm:text-[11px] font-semibold uppercase tracking-[0.28em] text-white/85">
              <span className="w-6 h-px bg-ice" />
              {language === 'en' ? 'Swiss Cross-Country Magazine' : 'Schweizer Langlauf-Magazin'}
            </span>
            <h1 className="font-display font-semibold text-[2.2rem] sm:text-5xl md:text-6xl lg:text-[4.75rem] xl:text-[5.5rem] leading-[1.02] tracking-tight text-white max-w-5xl">
              {headline}
            </h1>
            <p className="mt-6 sm:mt-8 max-w-xl text-base sm:text-lg md:text-xl text-white/85 font-light leading-relaxed">
              {sub}
            </p>

            <div className="mt-8 sm:mt-10 flex flex-wrap gap-3 sm:gap-4">
              <a
                href="#stories"
                onClick={handleExplore}
                className="inline-flex items-center gap-3 px-7 sm:px-8 py-3.5 sm:py-4 rounded-full bg-white text-ink font-medium text-sm sm:text-base shadow-2xl hover:bg-snow transition-all duration-300 hover:-translate-y-0.5"
              >
                {language === 'en' ? 'Discover glide' : 'glide entdecken'}
                <ArrowRight className="w-4 h-4" />
              </a>
              <Link
                to="/shop#abonnements"
                className="inline-flex items-center gap-3 px-7 sm:px-8 py-3.5 sm:py-4 rounded-full border border-white/40 text-white font-medium text-sm sm:text-base backdrop-blur-sm hover:bg-white/10 transition-all duration-300"
              >
                {language === 'en' ? 'Subscribe now' : 'Jetzt abonnieren'}
              </Link>
            </div>
          </div>

          {/* Cover column — 3D magazine mockup */}
          <div className="lg:col-span-4 hidden md:flex justify-center lg:justify-end">
            <div className="relative" style={{ perspective: '1600px' }}>
              {/* Glow */}
              <div className="absolute -inset-10 bg-ice/25 rounded-[40px] blur-3xl" aria-hidden />
              {/* Floor shadow */}
              <div
                aria-hidden
                className="absolute left-1/2 -translate-x-1/2 bottom-[-32px] h-10 w-[75%] rounded-[50%] bg-black/60 blur-2xl opacity-60"
              />

              <div
                className="relative animate-[floaty_8s_ease-in-out_infinite]"
                style={{ transform: 'rotateY(-18deg) rotateX(4deg)', transformStyle: 'preserve-3d' }}
              >
                {/* Back pages */}
                <div
                  aria-hidden
                  className="absolute inset-0 rounded-[6px] bg-white/60 shadow-[0_10px_40px_-15px_rgba(0,0,0,0.6)]"
                  style={{ transform: 'translateZ(-12px) translateX(6px)' }}
                />
                <div
                  aria-hidden
                  className="absolute inset-0 rounded-[6px] bg-white/80 shadow-[0_15px_50px_-20px_rgba(0,0,0,0.6)]"
                  style={{ transform: 'translateZ(-6px) translateX(3px)' }}
                />
                {/* Cover */}
                <div className="relative rounded-[6px] overflow-hidden bg-white shadow-[0_50px_90px_-30px_rgba(0,0,0,0.7),0_20px_40px_-20px_rgba(0,0,0,0.5)] w-[220px] md:w-[260px] lg:w-[300px] xl:w-[320px] aspect-[3/4]">
                  <img
                    src={currentCover}
                    alt={language === 'en' ? 'glide current issue' : 'glide aktuelle Ausgabe'}
                    className="w-full h-full object-cover"
                    loading="eager"
                  />
                  <div aria-hidden className="absolute inset-y-0 left-0 w-3 bg-gradient-to-r from-black/40 via-black/10 to-transparent" />
                  <div aria-hidden className="absolute inset-0 bg-gradient-to-tr from-white/10 via-transparent to-white/20 mix-blend-overlay" />
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* subtle scroll cue */}
      <div className="absolute bottom-5 left-1/2 -translate-x-1/2 z-10 hidden sm:flex flex-col items-center gap-2 text-white/60">
        <span className="text-[10px] uppercase tracking-[0.3em]">
          {language === 'en' ? 'Scroll' : 'Weiter'}
        </span>
        <span className="w-px h-8 bg-white/40" />
      </div>

      <style>{`
        @keyframes floaty {
          0%, 100% { transform: rotateY(-18deg) rotateX(4deg) translateY(0px); }
          50%      { transform: rotateY(-18deg) rotateX(4deg) translateY(-8px); }
        }
      `}</style>
    </section>
  );
};

export default Hero;
